import React from 'react';
import { motion } from 'framer-motion';
import './InsidePlatformSection.css';

const InsidePlatformSection = () => {
  // Function to get SVG content for each highlight
  const getSvgForHighlight = (index) => {
    const svgs = [
      // Orderflow with COT, IB/IS signals
      <svg key="orderflow" viewBox="0 0 24 24" width="32" height="32" stroke="#3A86FF" strokeWidth="1.5" fill="none">
        <rect x="3" y="8" width="4" height="12" rx="1" />
        <rect x="9" y="5" width="4" height="15" rx="1" />
        <rect x="15" y="2" width="4" height="18" rx="1" />
        <path d="M3 14h18" strokeDasharray="2 2" />
        <circle cx="6" cy="6" r="1" fill="#3A86FF" />
        <circle cx="18" cy="18" r="1" fill="#3A86FF" />
      </svg>,
      
      // MarketProfile with context and volume clusters
      <svg key="marketprofile" viewBox="0 0 24 24" width="32" height="32" stroke="#3A86FF" strokeWidth="1.5" fill="none">
        <path d="M2 12h4v8H2z" />
        <path d="M8 8h4v12H8z" />
        <path d="M14 4h4v16h-4z" />
        <path d="M20 2v20" strokeDasharray="1 1" />
        <path d="M2 16h20" strokeDasharray="1 1" />
        <circle cx="6" cy="16" r="1" fill="#3A86FF" />
        <circle cx="10" cy="12" r="1" fill="#3A86FF" />
        <circle cx="16" cy="8" r="1" fill="#3A86FF" />
      </svg>,
      
      // 11,000+ NSE instruments
      <svg key="instruments" viewBox="0 0 24 24" width="32" height="32" stroke="#3A86FF" strokeWidth="1.5" fill="none">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2v4" />
        <path d="M12 18v4" />
        <path d="M4.93 4.93l2.83 2.83" />
        <path d="M16.24 16.24l2.83 2.83" />
        <path d="M2 12h4" />
        <path d="M18 12h4" />
        <path d="M4.93 19.07l2.83-2.83" />
        <path d="M16.24 7.76l2.83-2.83" />
        <circle cx="12" cy="12" r="4" />
        <text x="12" y="14" textAnchor="middle" fontSize="6" fill="#3A86FF">11K+</text>
      </svg>,
      
      // Live scanners and structure-based planning
      <svg key="scanners" viewBox="0 0 24 24" width="32" height="32" stroke="#3A86FF" strokeWidth="1.5" fill="none">
        <circle cx="12" cy="12" r="3" />
        <path d="M3 12h3" />
        <path d="M15 12h6" />
        <path d="M12 3v3" />
        <path d="M12 15v6" />
        <path d="M3 3l18 18" />
        <path d="M21 3L3 21" />
        <circle cx="12" cy="12" r="1" fill="#3A86FF" />
      </svg>
    ];
    return svgs[index] || null;
  };

  // Enhanced highlights with better descriptions
  const highlights = [
    {
      title: "Orderflow with COT, IB/IS signals",
      description: "Read market intent, not just price action"
    },
    {
      title: "MarketProfile with context and volume clusters",
      description: "Understand market structure at a glance"
    },
    {
      title: "11,000+ NSE instruments",
      description: "Complete coverage of derivatives market"
    },
    {
      title: "Live scanners and structure-based planning",
      description: "Real-time opportunities identification"
    }
  ];

  return (
    <section className="inside-platform-section" id="inside-platform" aria-labelledby="platform-section-title">
      {/* Animated background elements */}
      <div className="gradient-element" aria-hidden="true"></div>
      <div className="gradient-element" aria-hidden="true"></div>
      <div className="glow-orb left" aria-hidden="true"></div>
      <div className="glow-orb right" aria-hidden="true"></div>
      
      <div className="container">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="subtitle">ADVANCED MARKET ANALYSIS</span>
          <h2 id="platform-section-title">See Through the Market. <span className="text-accent">Not Just At It.</span></h2>
          <p>Discover the powerful tools that make Vtrender<sup>™</sup> the preferred choice for NSE derivatives traders</p>
        </motion.div>
        
        {/* Platform description - improved layout */}
        <motion.div
          className="platform-description"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p className="tools-title">Tools you won't find elsewhere (and why traders switch):</p>
          <div className="tools-grid">
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>IB/IS (initiative buying/selling)</strong> — read intent, not just candles
              </div>
            </div>
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>MFlow<sup>™</sup></strong> — real-time orderflow map
              </div>
            </div>
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>Density & Gamma (±1σ tails)</strong> — frame the day's stretch and pick strikes with intent
              </div>
            </div>
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>Spectrum</strong> — options structure at a glance
              </div>
            </div>
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>NTM VolX<sup>™</sup></strong> — deeper tape/volume cues
              </div>
            </div>
            <div className="tool-item">
              <span className="tool-bullet">•</span>
              <div className="tool-content">
                <strong>Market Profile</strong> with KRLs, bookmarks, and multi-TF structure for fast context
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Key highlights in premium cards */}
        <motion.div
          className="premium-highlights-container"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="premium-highlights-grid">
            {highlights.map((highlight, index) => (
              <motion.div 
                className="premium-highlight-card"
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
              >
                <div className="highlight-icon">
                  <div className="svg-icon">
                    {getSvgForHighlight(index)}
                  </div>
                </div>
                <div className="highlight-content">
                  <h3>{highlight.title}</h3>
                  <p className="highlight-description">{highlight.description}</p>
                  <div className="highlight-line"></div>
                </div>
                <div className="highlight-glow"></div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsidePlatformSection;
